import { createContext, useContext, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { NativeSelect, TextInput } from "@mantine/core";
import { apiClient, apiErrorMessage } from "../api/client";
import type { EditableField } from "../api/types";
import { shortDate } from "../lib/format";
import { Button } from "./Button";

/* The Details tab of a record: the record's facts in named groups, read or
 * edited in place.
 *
 * The first design of 2026-10-08 gave every correctable fact its own Edit
 * link, a separate form for a phone, and the job corrected on another tab.
 * James, the same day: that is the lake's structure showing through, and a
 * person's model is "edit this record." So one Edit among the record's
 * actions puts this tab into edit mode: the same groups, the facts that can
 * change become inputs, one note for the whole edit, one Save. The page
 * holds the draft and sends what changed to api.edit_record (engine
 * migration 188), which parses it into whatever rows it has to be under one
 * signal with the editor's name. docs/ux-decisions.md, Records: viewing and
 * editing.
 *
 * WHICH FACTS CAN CHANGE
 *
 * A Fact given its column name becomes an input in edit mode when
 * api.editable_field has a row for that column: a menu with the engine's
 * allowed values in Buzze's words, yes or no, or a text box. The page
 * declares nothing about which columns those are. A Fact given an `edit`
 * element becomes that element instead, which is how a person's job and
 * their phones and emails, which are rows of their own in the lake, take
 * part in the same edit.
 *
 * WHAT A PERSON SEES THAT DISAGREES
 *
 * When another source has made a claim against a value a person edited,
 * the resolver keeps the claim beside the edit (identity.contested), and
 * api.contested_facts returns it. A fact that has one shows it under its
 * value with its source and date and one link, "Use it," which is an edit
 * with that value. */

export type Claim = {
  field: string;
  value: string | null;
  as_of: string | null;
  source_name: string | null;
  source_tier: string | null;
  signal_id: string | null;
};

type Words = Record<string, Record<string, string>>;

type Ctx = {
  specs: Map<string, EditableField>;
  words: Words;
  values: Record<string, unknown>;
  editing: boolean;
  draft: Record<string, string>;
  onDraft: (field: string, value: string) => void;
  contested: Record<string, Claim[]>;
  onUseClaim: ((field: string, value: string) => void) | undefined;
  saved: readonly string[];
};

const FactsContext = createContext<Ctx | null>(null);

/** The fields the engine lets a person correct on one kind of record. The
 *  list changes when a migration changes it, which is not within a session,
 *  so it is fetched once and kept. */
export function useEditableFields(token: string, entityType: string) {
  return useQuery({
    queryKey: ["editable-fields", entityType],
    staleTime: Infinity,
    queryFn: async () => {
      const { data, error } = await apiClient(token)
        // Bounded like every other query here. The view has 27 rows across
        // its four entities, so this will not truncate.
        .from("editable_field").select("*").eq("entity_type", entityType).limit(200);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as EditableField[];
    },
  });
}

/** The claims other sources have made against a person's edits on this
 *  record, by field. */
export function useContestedFacts(token: string, entityType: string, entityId: string) {
  return useQuery({
    queryKey: ["contested-facts", entityType, entityId],
    queryFn: async () => {
      const { data, error } = await apiClient(token)
        .rpc("contested_facts", { p_entity_type: entityType, p_entity_id: entityId }, { get: true })
        .limit(200);
      if (error) throw new Error(apiErrorMessage(error));
      const by: Record<string, Claim[]> = {};
      for (const c of (data ?? []) as Claim[]) (by[c.field] ??= []).push(c);
      return by;
    },
  });
}

/** The value of a column as the text an input shows: empty for nothing,
 *  "true" or "false" for a flag, the digits for a number. */
export function asText(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "boolean") return v ? "true" : "false";
  return String(v);
}

/** The Details tab's frame: groups of facts, laid out in columns where the
 *  screen is wide enough and one under another where it is not. */
export function Facts({
  token, entityType, values, words = {}, editing = false, draft = {}, onDraft, contested = {}, onUseClaim, saved = [], children,
}: {
  token: string;
  entityType: string;
  /** The record as the api view returned it. */
  values: Record<string, unknown>;
  /** Words for the allowed values of a field, by field then value. */
  words?: Words;
  editing?: boolean;
  /** What the person has typed so far, by column, while editing. */
  draft?: Record<string, string>;
  onDraft?: (field: string, value: string) => void;
  contested?: Record<string, Claim[]>;
  onUseClaim?: (field: string, value: string) => void;
  /** Columns saved a moment ago, marked beside their new value. */
  saved?: readonly string[];
  children: ReactNode;
}) {
  const fields = useEditableFields(token, entityType);
  const specs = new Map<string, EditableField>();
  for (const f of fields.data ?? []) if (f.field) specs.set(f.field, f);
  const ctx: Ctx = {
    specs, words, values, editing, draft,
    onDraft: onDraft ?? (() => undefined),
    contested, onUseClaim, saved,
  };
  return (
    <FactsContext.Provider value={ctx}>
      {fields.isError ? (
        <p className="refusal" role="alert">
          Could not load which fields can be corrected. {(fields.error as Error).message}
        </p>
      ) : null}
      <div className={editing ? "facts facts-editing" : "facts"}>{children}</div>
    </FactsContext.Provider>
  );
}

/** A named group of facts. */
export function FactGroup({ title, id, children }: { title: string; id?: string; children: ReactNode }) {
  return (
    <section className="fg" id={id} aria-label={title}>
      <h2 className="fg-title">{title}</h2>
      <div className="fg-body">{children}</div>
    </section>
  );
}

/** One fact: a label and a value. With `field`, the column the value came
 *  from, so that in edit mode it becomes an input when the engine lets that
 *  column be corrected, and so that a claim another source made against it
 *  shows under it. With `edit`, the element it becomes in edit mode instead.
 *  `id` goes on the label, for a header action that scrolls here. */
export function Fact({ label, field, id, edit, children }: {
  label: string;
  field?: string;
  id?: string;
  edit?: ReactNode;
  children: ReactNode;
}) {
  const ctx = useContext(FactsContext);
  const spec = field && ctx ? ctx.specs.get(field) : undefined;
  const claims = field && ctx ? ctx.contested[field] ?? [] : [];
  const justSaved = Boolean(field && ctx?.saved.includes(field));

  let body: ReactNode;
  if (ctx?.editing && edit !== undefined) {
    body = edit;
  } else if (ctx?.editing && spec && field) {
    body = <FactInput spec={spec} field={field} label={label} />;
  } else {
    body = (
      <>
        {children}
        {justSaved ? <span className="fact-saved" role="status">Saved</span> : null}
        {claims.length ? (
          <ul className="fact-claims">
            {claims.map((c, i) => (
              <li key={`${c.signal_id ?? i}`}>
                <span className="muted">
                  {c.source_name ?? "Another source"}{c.as_of ? <>, {shortDate(c.as_of)},</> : null} says
                </span>{" "}
                <span className="fact-claim-value">{c.value ?? "nothing"}</span>
                {ctx?.onUseClaim && c.value && field ? (
                  <Button kind="link" size="compact-xs" className="fact-use" onClick={() => ctx.onUseClaim?.(field, c.value as string)}>
                    Use it
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
      </>
    );
  }
  const quiet = Boolean(ctx?.editing && edit === undefined && !spec);
  return (
    <div className={quiet ? "fact fact-quiet" : "fact"}>
      <div className="fact-label" id={id}>{label}</div>
      <div className="fact-value">{body}</div>
    </div>
  );
}

/* The input for a column the engine lets a person correct, from its spec:
   a menu for allowed values, yes or no for a flag, a box for the rest. */
function FactInput({ spec, field, label }: { spec: EditableField; field: string; label: string }) {
  const ctx = useContext(FactsContext) as Ctx;
  const value = ctx.draft[field] ?? asText(ctx.values[field]);
  const set = (v: string) => ctx.onDraft(field, v);
  const options = (spec.allowed_values ?? []) as string[];
  const wordFor = (v: string) => ctx.words[field]?.[v] ?? v;
  // Empty is offered only where the lake will accept it, because a choice
  // the write refuses is a dead end on the screen.
  const notSet = spec.required ? [] : [{ value: "", label: "Not set" }];
  const id = `edit-${field}`;
  if (options.length > 0) {
    return (
      <NativeSelect id={id} aria-label={label} value={value} className="fact-input"
                    onChange={(e) => set(e.currentTarget.value)}
                    data={[...notSet, ...options.map((o) => ({ value: o, label: wordFor(o) }))]} />
    );
  }
  if (spec.data_type === "boolean") {
    return (
      <NativeSelect id={id} aria-label={label} value={value} className="fact-input"
                    onChange={(e) => set(e.currentTarget.value)}
                    data={[...notSet, { value: "true", label: "yes" }, { value: "false", label: "no" }]} />
    );
  }
  return (
    <TextInput id={id} aria-label={label} value={value} className="fact-input"
               inputMode={spec.data_type === "integer" ? "numeric" : undefined}
               onChange={(e) => set(e.currentTarget.value)} />
  );
}

/** A row of one or two link actions at the foot of a group. */
export function FactActions({ children }: { children: ReactNode }) {
  return <div className="fg-actions">{children}</div>;
}

/** A small form opened in place by one of the actions. */
export function FactForm({ label, children }: { label: string; children: ReactNode }) {
  return <div className="fact-form" role="group" aria-label={label}>{children}</div>;
}

/** What a tool said after it ran: a refusal, or a quiet confirmation. */
export function FactSaid({ error, children }: { error?: boolean; children: ReactNode }) {
  return (
    <p className={error ? "refusal fact-said" : "muted fact-said"} role={error ? "alert" : "status"}>
      {children}
    </p>
  );
}

/** The bar above the facts while editing: what saving means, a note for the
 *  edit, and Save and Cancel. */
export function EditBar({ pending, saving, error, note, onNote, onSave, onCancel }: {
  /** How many things the Save would change. */
  pending: number;
  saving: boolean;
  error: string | null;
  note: string;
  onNote: (n: string) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="edit-bar" role="region" aria-label="Editing this record">
      <p className="edit-bar-what">
        Change what is wrong and save once. It is saved with your name and today's date, and kept
        unless a more trustworthy source says otherwise.
      </p>
      <div className="edit-bar-row">
        <TextInput label="A note, if it helps the next person" value={note} w="22rem"
                   onChange={(e) => onNote(e.currentTarget.value)} />
        <Button kind="orange" onClick={onSave} disabled={saving || pending === 0}>
          {saving ? "Saving" : pending ? `Save ${pending} change${pending === 1 ? "" : "s"}` : "Save"}
        </Button>
        <Button kind="link" onClick={onCancel} disabled={saving}>Cancel</Button>
      </div>
      {error ? <p className="refusal" role="alert">{error}</p> : null}
    </div>
  );
}
